/**
 * Outbound message senders.
 *
 * One ergonomic wrapper per client-to-server message, each building its payload
 * with a factory from protocol.senders.generated.ts so a protocol change is a
 * compile error rather than a silent mismatch. Kept out of WebSocket.tsx because
 * they depend on nothing but `send`.
 */

import * as msg from "./protocol.senders.generated";
import type { Message, UpdateLocomotionMessage } from "./types";

export function createSenders(send: (message: Message) => void) {
  return {
    sendGuiEvent: (
      uuid: string,
      eventType: "click" | "change" | "update",
      value: unknown,
    ) => {
      send(msg.guiEvent({ uuid, event_type: eventType, value }));
    },

    sendTransformEvent: (
      uuid: string,
      position: [number, number, number],
      quaternion: [number, number, number, number],
    ) => {
      send(msg.transformEvent({ uuid, position, quaternion }));
    },

    sendClickEvent: (uuid: string, point: [number, number, number]) => {
      send(msg.clickEvent({ uuid, point }));
    },

    sendAddModule: (
      moduleId: string,
      position?: [number, number, number],
      instanceId?: string,
    ) => {
      send(
        msg.addModule({
          module_id: moduleId,
          position: position ?? [0, 0, 0],
          prefix: null,
          instance_id: instanceId ?? null,
        }),
      );
    },

    sendPreviewConnection: (
      childInstanceId: string,
      childLinkName: string,
      parentInstanceId: string,
      parentLinkName: string,
      showPreview: boolean,
      yaw: number = 0,
      pitch: number = 0,
      roll: number = 0,
      offset: [number, number, number] = [0, 0, 0],
    ) => {
      send(
        msg.previewConnection({
          child_instance_id: childInstanceId,
          child_link_name: childLinkName,
          parent_instance_id: parentInstanceId,
          parent_link_name: parentLinkName,
          show_preview: showPreview,
          yaw,
          pitch,
          roll,
          offset,
        }),
      );
    },

    sendConnectModules: (
      childInstanceId: string,
      childLinkName: string,
      parentInstanceId: string,
      parentLinkName: string,
      yaw: number = 0,
      pitch: number = 0,
      roll: number = 0,
      offset: [number, number, number] = [0, 0, 0],
    ) => {
      send(
        msg.connectModules({
          child_instance_id: childInstanceId,
          child_link_name: childLinkName,
          parent_instance_id: parentInstanceId,
          parent_link_name: parentLinkName,
          yaw,
          pitch,
          roll,
          offset,
        }),
      );
    },

    sendSelectInstance: (instanceId: string | null) => {
      send(msg.selectInstance({ instance_id: instanceId ?? "" }));
    },

    sendUpdateInstancePosition: (
      instanceId: string,
      position: [number, number, number],
    ) => {
      send(msg.updateInstancePosition({ instance_id: instanceId, position }));
    },

    sendTranslationEnd: () => {
      send(msg.translationEnd());
    },

    sendDeleteInstance: (instanceId: string) => {
      send(msg.deleteInstance({ instance_id: instanceId }));
    },

    sendDisconnectInstance: (instanceId: string) => {
      send(msg.disconnectInstance({ instance_id: instanceId }));
    },

    sendClearAllInstances: () => {
      send(msg.clearAllInstances());
    },

    sendSetInstancePrefix: (instanceId: string, prefix: string | null) => {
      send(msg.setInstancePrefix({ instance_id: instanceId, prefix }));
    },

    sendSetInstanceParameter: (
      instanceId: string,
      parameter: string,
      value: number,
    ) => {
      send(
        msg.setInstanceParameter({ instance_id: instanceId, parameter, value }),
      );
    },

    sendEnableIKMode: (enabled: boolean) => {
      send(msg.enableIkMode({ enabled }));
    },

    sendEnableSelfCollision: (enabled: boolean) => {
      send(msg.enableSelfCollision({ enabled }));
    },

    sendEnableJointAxes: (enabled: boolean) => {
      send(msg.enableJointAxes({ enabled }));
    },

    sendSetJointAxisReversed: (jointName: string, axisReversed: boolean) => {
      send(
        msg.setJointAxisReversed({
          joint_name: jointName,
          axis_reversed: axisReversed,
        }),
      );
    },

    sendEnterEditModuleAxes: (instanceId: string) => {
      send(msg.enterEditModuleAxes({ instance_id: instanceId }));
    },

    sendSaveModuleAxes: (moduleId: string, reversedJoints: string[]) => {
      send(
        msg.saveModuleAxes({
          module_id: moduleId,
          reversed_joints: reversedJoints,
        }),
      );
    },

    sendIKGizmoUpdate: (
      gizmoId: string,
      position: [number, number, number],
      quaternion: [number, number, number, number],
    ) => {
      send(msg.ikGizmoUpdate({ gizmo_id: gizmoId, position, quaternion }));
    },

    sendSetIKConstraints: (
      positionMask: [boolean, boolean, boolean],
      rotationMask: [boolean, boolean, boolean],
    ) => {
      send(
        msg.setIkConstraints({
          position_mask: positionMask,
          rotation_mask: rotationMask,
        }),
      );
    },

    sendJointSliderUpdate: (jointName: string, value: number) => {
      send(msg.jointSliderUpdate({ joint_name: jointName, value }));
    },

    sendSaveConfig: (configName: string) => {
      send(msg.saveConfig({ config_name: configName }));
    },

    sendLoadConfig: (configData: string, merge: boolean = false) => {
      send(msg.loadConfig({ config_data: configData, merge }));
    },

    sendEnterEditLinksMode: (instanceId: string) => {
      send(msg.enterEditLinksMode({ instance_id: instanceId }));
    },

    sendExitEditLinksMode: () => {
      send(msg.exitEditLinksMode());
    },

    sendSaveConnectableLinks: (
      moduleId: string,
      connectableLinks: string[],
      canBeChild: boolean,
      hasServo: boolean,
      endEffectorMode: "auto" | "none" | "custom",
      endEffectorLink: string | null,
      endEffectorOffsetXyz: [number, number, number],
      endEffectorOffsetRpy: [number, number, number],
    ) => {
      send(
        msg.saveConnectableLinks({
          module_id: moduleId,
          connectable_links: connectableLinks,
          can_be_child: canBeChild,
          has_servo: hasServo,
          end_effector_mode: endEffectorMode,
          end_effector_link: endEffectorLink,
          end_effector_offset_xyz: endEffectorOffsetXyz,
          end_effector_offset_rpy: endEffectorOffsetRpy,
        }),
      );
    },

    sendAddEndCoords: (
      name: string,
      parentLinkName: string,
      position: [number, number, number],
      rotation: [number, number, number],
    ) => {
      send(
        msg.addEndCoords({
          name,
          parent_link_name: parentLinkName,
          position,
          rotation,
        }),
      );
    },

    sendUpdateEndCoords: (
      name: string,
      parentLinkName: string,
      position: [number, number, number],
      rotation: [number, number, number],
    ) => {
      send(
        msg.updateEndCoords({
          name,
          parent_link_name: parentLinkName,
          position,
          rotation,
        }),
      );
    },

    sendDeleteEndCoords: (name: string) => {
      send(msg.deleteEndCoords({ name }));
    },

    sendHighlightInstance: (instanceId: string) => {
      send(msg.highlightInstance({ instance_id: instanceId }));
    },

    sendShowEndCoordsGizmo: (
      show: boolean,
      parentLinkName: string,
      position: [number, number, number],
      rotation: [number, number, number],
    ) => {
      send(
        msg.showEndCoordsGizmo({
          show,
          parent_link_name: parentLinkName,
          position,
          rotation,
        }),
      );
    },

    sendEndCoordsGizmoUpdate: (
      position: [number, number, number],
      quaternion: [number, number, number, number],
    ) => {
      send(msg.endCoordsGizmoUpdate({ position, quaternion }));
    },

    sendRenameJoint: (jointName: string, newName: string) => {
      send(msg.renameJoint({ joint_name: jointName, new_name: newName }));
    },

    sendCreateJointGroup: (
      name: string,
      jointNames: string[],
      color: string | null,
    ) => {
      send(msg.createJointGroup({ name, joint_names: jointNames, color }));
    },

    sendUpdateJointGroup: (
      name: string,
      newName: string | null,
      jointNames: string[],
      color: string | null,
    ) => {
      send(
        msg.updateJointGroup({
          name,
          new_name: newName,
          joint_names: jointNames,
          color,
        }),
      );
    },

    sendDeleteJointGroup: (name: string) => {
      send(msg.deleteJointGroup({ name }));
    },

    sendDetectKinematicChains: () => {
      send(msg.detectKinematicChains());
    },

    sendUpdateJointSettings: (
      jointName: string,
      servoId: number | null,
      direction: 1 | -1,
      angleOffset: number,
      customMinAngle: number | null,
      customMaxAngle: number | null,
    ) => {
      send(
        msg.updateJointSettings({
          joint_name: jointName,
          servo_id: servoId,
          direction,
          angle_offset: angleOffset,
          custom_min_angle: customMinAngle,
          custom_max_angle: customMaxAngle,
        }),
      );
    },

    sendUpdateMimicSettings: (
      jointName: string,
      mimicJoint: string | null,
      mimicMultiplier: number,
      mimicOffset: number,
    ) => {
      send(
        msg.updateMimicSettings({
          joint_name: jointName,
          mimic_joint: mimicJoint,
          mimic_multiplier: mimicMultiplier,
          mimic_offset: mimicOffset,
        }),
      );
    },

    sendHighlightJoint: (jointName: string) => {
      send(msg.highlightJoint({ joint_name: jointName }));
    },

    sendGetChainJoints: (baseLink: string, tipLink: string) => {
      send(msg.getChainJoints({ base_link: baseLink, tip_link: tipLink }));
    },

    sendGetUrdfForConfig: () => {
      send(msg.getUrdfForConfig());
    },

    sendUploadModule: (
      name: string,
      dataBase64: string,
      fileType: "zip" | "urdf",
    ) => {
      send(
        msg.uploadModule({
          name,
          data_base64: dataBase64,
          file_type: fileType,
        }),
      );
    },

    /**
     * Edit the locomotion setup; the server answers with `locomotion_state`.
     * Every field is optional and `{}` just asks for the current state.
     */
    sendUpdateLocomotion: (
      fields: Partial<Omit<UpdateLocomotionMessage, "type">>,
    ) => {
      send(
        msg.updateLocomotion({
          set_home_pose_from_current: false,
          clear_home_pose: false,
          forward_world: null,
          feet: null,
          auto_detect_feet: false,
          instances_key: null,
          ...fields,
        }),
      );
    },
  };
}

export type Senders = ReturnType<typeof createSenders>;
